import React from 'react';
import { ArrowLeft, ExternalLink, Coffee, Home, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CustomBrandingExample: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/custom-branding')}
            className="flex items-center gap-2 text-white/60 hover:text-[#fda36b] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Custom Branding
          </button>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="text-6xl mb-6">🏠</div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Custom Branding Example
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              See how your custom-branded coffee could look. This is a sample for a fictional real estate company.
            </p>
          </div>

          {/* Sample Custom Coffee */}
          <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-8 rounded-2xl border border-[#fda36b]/20 mb-12">
            <div className="space-y-8">
              {/* Header Section */}
              <div className="text-center">
                <h2 className="text-3xl font-bold text-[#fda36b] mb-4">
                  Welcome Home Blend
                </h2>
                <div className="mb-4 space-y-2">
                  <div className="inline-block bg-blue-500/20 px-3 py-1 rounded-full mr-2">
                    <span className="text-blue-400 font-semibold text-sm">Custom Branded</span>
                  </div>
                  <div className="inline-block bg-green-500/20 px-3 py-1 rounded-full mr-2">
                    <span className="text-green-400 font-semibold text-sm">Real Estate Company</span>
                  </div>
                  <div className="inline-block bg-[#fda36b]/20 px-3 py-1 rounded-full">
                    <span className="text-[#fda36b] font-semibold text-sm">Based on Barnegat Breakfast</span>
                  </div>
                </div>
              </div>
              
              {/* Story and Details Grid */}
              <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
                {/* Story Section - Takes up 2 columns */}
                <div className="lg:col-span-2 space-y-4 lg:space-y-6">
                  <div className="space-y-4 lg:space-y-6 mb-4 lg:mb-6">
                    <p className="text-sm lg:text-base text-white/90 leading-relaxed">
                      <strong>Congratulations on your new home!</strong> Just like finding the perfect house, great coffee is about more than just the basics—it's about the story, the craft, and the care that goes into every detail.
                    </p>
                    
                    <p className="text-sm lg:text-base text-white/90 leading-relaxed">
                      At Real Estate Company, we believe that every home has a story, and every family deserves to find their perfect place. That's why we've partnered with Bond Coffee to create something special for our clients—a blend that represents the same attention to detail and commitment to quality that we bring to every closing.
                    </p>
                    
                    <p className="text-sm lg:text-base text-white/90 leading-relaxed">
                      This custom roast combines the rich, dependable foundation of Brazilian coffee with the bright complexity of Guatemalan beans—much like how we combine market expertise with personal service to help you find not just a house, but a home. Every bag is roasted fresh and delivered with love, because home is where the heart is, and great coffee brings families together.
                    </p>
                    
                    <p className="text-sm lg:text-base text-white/90 leading-relaxed">
                      <strong>Welcome to the community, and thank you for trusting Real Estate Company with your home journey.</strong> We hope this coffee brings warmth to your new kitchen, joy to your morning routine, and creates countless memories around your table. Here's to new beginnings and the perfect cup in your perfect home.
                    </p>
                  </div>
                </div>
                
                {/* Quick Info Sidebar */}
                <div className="space-y-4 lg:space-y-6">
                  {/* Mock Product Image */}
                  <div className="bg-white/5 p-2 rounded-lg border border-white/10 max-w-[250px] lg:max-w-none mx-auto lg:mx-0">
                    <div className="aspect-[3/4] overflow-hidden rounded-lg flex items-center justify-center p-4 bg-gradient-to-br from-blue-500/20 to-blue-600/10">
                      <div className="text-center space-y-3">
                        <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto">
                          <Home className="w-8 h-8 text-blue-400" />
                        </div>
                        <div className="text-white font-bold text-lg">Welcome Home</div>
                        <div className="text-white/80 text-sm">Blend</div>
                        <div className="text-blue-400 text-xs">Custom Roasted</div>
                        <div className="text-white/60 text-xs">Real Estate Company</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Sample QR Code Feature */}
                  <div className="bg-white/5 p-3 lg:p-4 rounded-lg border border-white/10">
                    <div className="text-center space-y-3">
                      <div className="w-20 h-20 bg-white/90 rounded-lg mx-auto flex items-center justify-center">
                        <div className="text-xs text-black font-mono">QR CODE</div>
                      </div>
                      <p className="text-white/80 text-xs lg:text-sm">
                        <strong>Scan to visit:</strong><br />
                        realestatecompany.com/welcome
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-white font-semibold mb-2 lg:mb-3 text-sm lg:text-base">Tasting Notes</h3>
                    <div className="grid grid-cols-2 gap-2 lg:gap-3">
                      <div className="bg-white/10 p-2 lg:p-3 rounded-lg text-center">
                        <div className="text-lg lg:text-2xl mb-1">🍫</div>
                        <div className="text-white/90 text-xs lg:text-sm">Chocolate</div>
                      </div>
                      <div className="bg-white/10 p-2 lg:p-3 rounded-lg text-center">
                        <div className="text-lg lg:text-2xl mb-1">🥜</div>
                        <div className="text-white/90 text-xs lg:text-sm">Nutty</div>
                      </div>
                      <div className="bg-white/10 p-2 lg:p-3 rounded-lg text-center">
                        <div className="text-lg lg:text-2xl mb-1">🍯</div>
                        <div className="text-white/90 text-xs lg:text-sm">Honey</div>
                      </div>
                      <div className="bg-white/10 p-2 lg:p-3 rounded-lg text-center">
                        <div className="text-lg lg:text-2xl mb-1">❤️</div>
                        <div className="text-white/90 text-xs lg:text-sm">Love</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-2 lg:mb-3 text-sm lg:text-base">Custom Details</h3>
                    <div className="space-y-1 lg:space-y-2 text-xs lg:text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/70">Base Roast:</span>
                        <span className="text-white">Barnegat Breakfast</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Custom Label:</span>
                        <span className="text-white">✓ Included</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">QR Landing:</span>
                        <span className="text-white">✓ Included</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Fresh Roasted:</span>
                        <span className="text-white">On Demand</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Custom Features */}
              <div>
                <h3 className="text-white font-semibold mb-6 text-center">What Makes This Special</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 text-center">
                    <Home className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">Your Brand Story</h4>
                    <p className="text-white/80 text-sm">
                      Custom message that connects your values to the coffee experience
                    </p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 text-center">
                    <ExternalLink className="w-8 h-8 text-[#fda36b] mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">QR Code Landing</h4>
                    <p className="text-white/80 text-sm">
                      Links to your personalized page with your story and contact info
                    </p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 text-center">
                    <Coffee className="w-8 h-8 text-[#fda36b] mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-2">Premium Quality</h4>
                    <p className="text-white/80 text-sm">
                      Same obsessive attention to quality as our retail blends
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* This is Just an Example */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 p-8 rounded-2xl border border-white/20 mb-12">
            <div className="text-center space-y-6">
              <Star className="w-12 h-12 text-[#fda36b] mx-auto" />
              <h2 className="text-3xl font-bold text-white">
                This is Just One Example
              </h2>
              <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                <strong>This Real Estate Company is fictional</strong> — but this shows exactly what your custom-branded coffee could look like. 
                We'll work with you to create the perfect story, design, and message for your business.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto text-left">
                <div className="space-y-3">
                  <h3 className="text-[#fda36b] font-semibold">We Customize Everything:</h3>
                  <ul className="text-white/80 space-y-1 text-sm">
                    <li>• Your company name and logo</li>
                    <li>• Your brand colors and design</li>
                    <li>• Your personalized story and message</li>
                    <li>• QR code linking to your website/landing page</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="text-[#fda36b] font-semibold">Perfect For:</h3>
                  <ul className="text-white/80 space-y-1 text-sm">
                    <li>• Real estate closing gifts</li>
                    <li>• Home service thank-you gifts</li>
                    <li>• Financial advisor client appreciation</li>
                    <li>• Law firm case completion gifts</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="bg-gradient-to-br from-[#fda36b]/20 to-[#fda36b]/10 p-8 rounded-2xl border border-[#fda36b]/30">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Create Your Custom Coffee?
              </h2>
              <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
                Let's design something amazing for your business. From concept to delivery, we make the process simple.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:hello@thebondcoffee.com?subject=Custom Branding Inquiry"
                  className="bg-[#fda36b] text-[#132947] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#fda36b]/90 transition-all duration-300"
                >
                  Start Your Custom Coffee
                </a>
                <button
                  onClick={() => navigate('/custom-branding')}
                  className="bg-white/10 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300"
                >
                  Learn More About Custom Branding
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CustomBrandingExample;